/* @flow */
import idx from 'idx';
import cx from 'classnames';
import { graphql } from 'react-apollo';
import * as React from 'react';
import Text from 'components/Text';
import { unionBy, flatten } from 'lodash';
import ShortcodeEmoji from 'components/ShortcodeEmoji';
import reactionSettings from 'user/queries/reactionSettings.gql';

import './styles/MessageReactions.scss';

export const generateReactionSummaryDescription = (
  reactionSummary: ?ReactionSummaryGQL,
  currentUserId?: ?string
) => {
  if (!reactionSummary || !reactionSummary.reactions || reactionSummary.reactions.length < 1) {
    return 'No-one has reacted yet. Be the first!';
  } else {
    // At least one reaction exists, pull out all users and de-dupe
    const { reactions } = reactionSummary;
    const userNames = unionBy(flatten(reactions.map(r => r.users)), 'uuid').map(u => {
      return u.uuid === currentUserId ? 'You' : u.name;
    });

    const firstUserName = userNames[0];

    // This can be caused by hermes timeouts. Since we have no useful information to show, just abort
    // https://app.asana.com/0/32507888495125/395443531020487
    if (userNames.length === 0) {
      return null;
    } else if (userNames.length === 1) {
      return `${firstUserName} ${firstUserName === 'You' ? 'have' : 'has'} reacted`;
    } else if (userNames.length === 2) {
      return `${firstUserName} and ${userNames[1]} have reacted`;
    } else {
      return `${firstUserName} & ${userNames.length - 1} others reacted`;
    }
  }
};

type Props = {
  recipientUuid?: ?string,
  reactionSummary?: ?ReactionSummaryGQL,
  addReactionToSummary: Function,
  removeReactionFromSummary: Function,
  availableReactions: ReactionInventoryItem[],
};

class MessageReactions extends React.Component<Props> {
  toggleReaction = (reaction: ReactionInventoryItem, hasReacted: boolean): void => {
    if (hasReacted) {
      this.props.removeReactionFromSummary(reaction.name);
    } else {
      this.props.addReactionToSummary(reaction.name);
    }
  };

  renderReaction = (reaction: ReactionInventoryItem): ?React.Element<any> => {
    const { reactionSummary } = this.props;

    const existingReaction =
      reactionSummary && reactionSummary.reactions.find(r => r.name === reaction.name);

    const reactionCount = existingReaction ? existingReaction.count : 0;
    const emojiCode = `:${reaction.name}:`;
    const hasReacted = !!(existingReaction && existingReaction.hasReacted);

    if (!reaction.active) {
      return null;
    }

    return (
      reaction.active && (
        <div
          className={cx('MessageReactions-reaction', {
            'MessageReactions-reaction--reacted': hasReacted,
          })}
          key={emojiCode}
          onClick={() => this.toggleReaction(reaction, hasReacted)}>
          <ShortcodeEmoji text={emojiCode} />
          {reactionCount ? (
            <div className="MessageReactions-reaction-count">{reactionCount}</div>
          ) : (
            <div className="MessageReactions-reaction-add">+</div>
          )}
        </div>
      )
    );
  };

  render() {
    const { availableReactions, recipientUuid, reactionSummary } = this.props;
    if (!availableReactions || availableReactions.length === 0) {
      return null;
    }

    const reactionSummaryDescription = generateReactionSummaryDescription(
      reactionSummary,
      recipientUuid
    );

    return (
      <div className="MessageReactions">
        <div className="MessageReactions-reactions">
          {availableReactions.map(this.renderReaction)}
        </div>
        <div className="MessageReactions-footer">
          <Text lineHeight="xlarge" size="xlarge" className="MessageReactions-footer-title">
            React to this message
          </Text>
          <Text color="eclipse" size="small" className="MessageReactions-footer-summary">
            {reactionSummaryDescription}
          </Text>
        </div>
      </div>
    );
  }
}

const withReactions = graphql(reactionSettings, {
  props: ({ data }) => {
    const availableReactions = idx(data, _ => _.settings.reactionInventory.reactions);
    if (!availableReactions) {
      return {};
    }

    return { availableReactions };
  },
});

export { MessageReactions };
export default withReactions(MessageReactions);
